import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, TextField, InputAdornment, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ModalClose } from '../assets/icons';
import AuthService from '../services/auth.service';
import { toast } from 'react-toastify';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    content?: string;
    onConfirm?: () => void;
    username?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, onConfirm, username }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Password visibility states
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validateForm = () => {
        if (!currentPassword) {
            setError('Current password is required');
            return false;
        }
        if (!newPassword) {
            setError('New password is required');
            return false;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        // Add any additional password validation requirements here
        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return false;
        }
        return true;
    };

    const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleChangePassword = async () => {
        try {
            setError('');
            
            if (!validateForm()) {
                return;
            }

            setLoading(true);
            
            if (!username) {
                setError('Could not authenticate user. Please try again.');
                setLoading(false);
                return;
            }
            
            const response = await AuthService.changePasswordAfterLogin({
                username,
                oldPassword: currentPassword,
                newPassword: newPassword
            });

            setLoading(false);
            setSuccess(true);
            
            // Reset form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            toast.success('Password changed successfully!');
            
            // Close modal after short delay
            setTimeout(() => {
                onClose();
                if (onConfirm) {
                    onConfirm();
                }
            }, 1500);
        } catch (err: any) {
            setLoading(false);
            toast.error(err.message || 'Failed to change password');
            setError(err.message || 'Failed to change password');
        }
    };

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setSuccess(false);
        // Reset visibility states
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} className='card-details-modal' sx={{ '& .MuiDialog-paper': { width: '661px', maxWidth: '661PX', borderRadius: '24px', border: '1px solid #E8E8EA' } }}>
                <DialogTitle sx={{ mb: "32px" }} p={{ xs: "16px 16px 15px !important" }} borderBottom="1px solid #E8EAF0">
                    <Typography color='#333333' fontSize={{ xs: "22px", sm: "28px" }} fontWeight='500' >Change Password</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={() => ({
                            position: 'absolute',
                            right: { xs: "17px" },
                            top: { xs: "15px" },
                        })}
                    >
                        <ModalClose></ModalClose>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "0 16px 16px", sm: "0 32px 32px" } }}>
                    {error && (
                        <Typography color="error" mb={2} fontSize="14px">
                            {error}
                        </Typography>
                    )}

                    <Grid spacing={3} container rowSpacing={{ xs: 12, sm: 12, md: 12 }} sx={{ marginBottom: "20px" }}>
                        {/* First row - One field */}
                        <Grid size={6}>
                            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px'>Current Password*</Typography>
                            <TextField
                                variant="outlined"
                                type={showCurrentPassword ? 'text' : 'password'}
                                placeholder='**********'
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                autoComplete="new-password"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: '8px',
                                        borderColor: '#C7C7C7',
                                    },

                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: '#333333',
                                        border: '1px solid'
                                    },

                                    "& .MuiOutlinedInput-input": {
                                        padding: '14px 16px',
                                    },
                                    "&.MuiInputBase-input::placeholder": {
                                        color: '#333333',
                                        fontSize: '16px',
                                        opacity: '0.8'
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowCurrentPassword}
                                                sx={{ borderRadius: "2px", "&:hover": { backgroundColor: "unset" }, padding: '4px' }}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}
                                                edge="end"
                                            >
                                                {!showCurrentPassword ? (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityIcon fontSize='small' />
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityOffIcon fontSize='small' />
                                                    </Box>
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Grid spacing={3} container rowSpacing={{ xs: 12, sm: 12, md: 12 }}>
                        <Grid size={6}>
                            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px'>New Password*</Typography>
                            <TextField
                                variant="outlined"
                                type={showNewPassword ? 'text' : 'password'}
                                placeholder='**********'
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: '8px',
                                        borderColor: '#C7C7C7',
                                    },

                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: '#333333',
                                        border: '1px solid'
                                    },

                                    "& .MuiOutlinedInput-input": {
                                        padding: '14px 16px',
                                    },
                                    "&.MuiInputBase-input::placeholder": {
                                        color: '#333333',
                                        fontSize: '16px',
                                        opacity: '0.8'
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowNewPassword}
                                                sx={{ borderRadius: "2px", "&:hover": { backgroundColor: "unset" }, padding: '4px' }}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}
                                                edge="end"
                                            >
                                                {!showNewPassword ? (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityIcon fontSize='small' />
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityOffIcon fontSize='small' />
                                                    </Box>
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={6}>
                            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px'>Confirm Password*</Typography>
                            <TextField
                                variant="outlined"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder='**********'
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: '8px',
                                        borderColor: '#C7C7C7',
                                    },

                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: '#333333',
                                        border: '1px solid'
                                    },

                                    "& .MuiOutlinedInput-input": {
                                        padding: '14px 16px',
                                    },
                                    "&.MuiInputBase-input::placeholder": {
                                        color: '#333333',
                                        fontSize: '16px',
                                        opacity: '0.8'
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowConfirmPassword}
                                                sx={{ borderRadius: "2px", "&:hover": { backgroundColor: "unset" }, padding: '4px' }}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}
                                                edge="end"
                                            >
                                                {!showConfirmPassword ? (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityIcon fontSize='small' />
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityOffIcon fontSize='small' />
                                                    </Box>
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" } }}>
                    <Button color="primary" onClick={handleClose} sx={{
                        border: '1px solid #E8EAF0', px: '22px', color: '#2B2B30 !important', textTransform: 'capitalize', borderRadius: '8px', mr: '16px', '&:hover': {
                            border: '1px solid #E8EAF0 !important',
                            boxShadow: 'unset !important'
                        },
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleChangePassword}
                        disabled={loading}
                        sx={{
                            textTransform: 'capitalize', 
                            bgcolor: '#178EFC', 
                            color: '#FFFFFF !important', 
                            px: '24px', 
                            height: '40px', 
                            borderRadius: '8px', 
                            '&:hover': {
                                boxShadow: 'none !important',
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#178EFC80',
                                color: '#FFFFFF !important',
                            }
                        }}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ChangePasswordModal;
